import graphql from 'babel-plugin-relay/macro';
import React from 'react';
import {usePaginationFragment, useMutation} from 'react-relay/hooks';
import ConnectionHandler from 'relay-connection-handler-plus';
import AssetStatusCell from 'src/components/table/cell-types/AssetStatusCell';
import ImportantTextCell from 'src/components/table/cell-types/ImportantTextCell';
import useFilters from 'src/hooks/useFilters';
import {showEventNotification} from 'src/utils/notifications';

import Table from '../../../components/table';
import SecondaryTextCell from '../../../components/table/cell-types/SecondaryTextCell';
import {AssetsOrder} from './__generated__/GeneralAssetsPaginationQuery.graphql';
import {GeneralAssetsTable_generalAssets$key} from './__generated__/GeneralAssetsTable_generalAssets.graphql';
import {GeneralAssetsTableDeleteGeneralAssetMutation} from './__generated__/GeneralAssetsTableDeleteGeneralAssetMutation.graphql';
import {listPagegeneralAssetsQuery} from './__generated__/listPagegeneralAssetsQuery.graphql';
import TableFilters from './TableFilters';

type Props = {
  generalAssets: GeneralAssetsTable_generalAssets$key;
};

type Filters = {
  text?: string;
  orderBy?: AssetsOrder;
};

function GeneralAssetsTable(props: Props) {
  const {data, loadNext, hasNext, refetch} = usePaginationFragment<
    listPagegeneralAssetsQuery,
    GeneralAssetsTable_generalAssets$key
  >(
    graphql`
      fragment GeneralAssetsTable_generalAssets on Query
        @argumentDefinitions(
          first: {type: "Int!"}
          after: {type: "String"}
          text: {type: "String"}
          orderBy: {type: "AssetsOrder"}
        )
        @refetchable(queryName: "GeneralAssetsPaginationQuery") {
        generalAssets(first: $first, after: $after, orderBy: $orderBy, text: $text)
          @connection(key: "GeneralAssetsList_generalAssets") {
          totalCount
          pageInfo {
            hasPreviousPage
            hasNextPage
            startCursor
            endCursor
          }
          edges {
            node {
              id
              createdAt
              serial
              externalId
              status
              model {
                id
                name
              }
              family {
                id
                name
              }
              manufacturer {
                id
                name
              }
            }
          }
        }
      }
    `,
    props.generalAssets,
  );

  const [commit] = useMutation<GeneralAssetsTableDeleteGeneralAssetMutation>(
    deleteGeneralAssetMutation,
  );

  const {addFilters, setFilters, isTransitioning} = useFilters<Filters>({}, refetch);

  return (
    <Table
      filters={() => (
        <TableFilters onApplyFilters={handleOnApplyFilter} onResetFilters={handleOnResetFilter} />
      )}
      actions={{show: true, delete: true, edit: true, create: true}}
      totalDataLength={data.generalAssets.totalCount}
      columns={columns}
      onPaginate={() => hasNext && loadNext(50)}
      onSort={handleOnSort}
      onDelete={handleDelete}
      isTransitioning={isTransitioning}
      data={data.generalAssets.edges}
    />
  );

  function handleDelete(id: string) {
    commit({
      variables: {input: {generalAssetId: id}},
      onCompleted: payload => {
        const entry = payload.deleteGeneralAsset?.generalAsset;

        if (entry) {
          showEventNotification('removed', 'Asset', entry.serial);
        }
      },
      updater: store => {
        const root = store.getRoot();

        const connections = ConnectionHandler.getConnections(
          root,
          'GeneralAssetsList_generalAssets',
        );

        if (!connections) {
          return;
        }

        connections.forEach(connection => {
          ConnectionHandler.deleteNode(connection, id);
        });
      },
    });
  }

  function handleOnApplyFilter(filters: {text: string}) {
    addFilters({text: filters.text});
  }

  function handleOnResetFilter() {
    setFilters({});
  }

  async function handleOnSort(sortByConfig: any[]) {
    if (!sortByConfig.length) {
      addFilters({orderBy: undefined});

      return;
    }

    const orderByDirection = sortByConfig[0].desc ? 'DESC' : 'ASC';
    const orderByField = sortByConfig[0].id.toUpperCase();

    addFilters({orderBy: {direction: orderByDirection, field: orderByField}});
  }
}

export default GeneralAssetsTable;

const deleteGeneralAssetMutation = graphql`
  mutation GeneralAssetsTableDeleteGeneralAssetMutation($input: DeleteGeneralAssetInput!) {
    deleteGeneralAsset(input: $input) {
      generalAsset {
        id
        serial
      }
      errors {
        code
        message
      }
    }
  }
`;

const columns = [
  {
    Header: 'ID',
    accessor: 'node.externalId',
    id: 'external_id',
    Cell({cell: {value}}: any) {
      return <ImportantTextCell value={value} />;
    },
  },
  {
    Header: 'Serial',
    accessor: 'node.serial',
    id: 'serial',
    Cell({cell: {value}}: any) {
      return <SecondaryTextCell value={value} />;
    },
  },
  {
    Header: 'Status',
    accessor: 'node.status',
    id: 'status',
    Cell({cell: {value}}: any) {
      return <AssetStatusCell value={value} />;
    },
  },
  {
    Header: 'Manufacturer',
    accessor: 'node.manufacturer.name',
    id: 'manufacturer_name',
    Cell({cell: {value}}: any) {
      return <SecondaryTextCell value={value} />;
    },
  },
  {
    Header: 'Type',
    accessor: 'node.family.name',
    id: 'family_name',
    Cell({cell: {value}}: any) {
      return <SecondaryTextCell value={value} />;
    },
  },
  {
    Header: 'Model',
    accessor: 'node.model.name',
    id: 'model_name',
    Cell({cell: {value}}: any) {
      return <SecondaryTextCell value={value} />;
    },
  },
];
